import { img } from "../images";
import type { Blog } from "./types";

const TEAM = "AV Clinic Medical Team";
const TEAM_BIO =
  "The AV Clinic Medical Team comprises the specialist doctors at AV Clinic, Krishnagiri, who provide patient-friendly health information across urology, andrology, dermatology and preventive care.";
const TEAM_ROLE = "Medical Team, AV Clinic, Krishnagiri";

const DR_ARUN = "Dr. M. V. Arun Vijaiyan";
const ARUN_BIO =
  "Dr. M. V. Arun Vijaiyan is a Urologist and Andrologist at AV Clinic, Krishnagiri, with an M.S. and M.Ch. (Uro). He focuses on kidney stone care, urinary and prostate health, and male reproductive and sexual health.";
const ARUN_ROLE = "Urologist & Andrologist at AV Clinic, Krishnagiri";

export const generalBlogs: Blog[] = [
  {
    slug: "regular-health-checkups",
    title: "Importance of Regular Health Checkups",
    category: "Preventive Healthcare",
    excerpt:
      "Regular health checkups help catch problems early. Learn why they matter and what a good checkup covers.",
    image: img.svcUroConsult,
    imageAlt: "Importance of regular health checkups",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-31",
    modified: "2026-08-31",
    readTime: "5 min read",
    tags: ["health checkup", "preventive care", "screening", "general health"],
    primaryKeyword: "regular health checkups",
    secondaryKeywords: ["health checkup Krishnagiri", "preventive health check", "full body checkup"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["healthy-lifestyle-adults", "symptoms-not-to-ignore", "choose-right-specialist"],
    sections: [
      {
        heading: "Prevention is easier than cure",
        body: [
          "Many health conditions develop quietly and are easier to manage when found early. Regular checkups create a chance to detect concerns before they become serious.",
        ],
      },
      {
        heading: "What a checkup can cover",
        body: ["A good checkup reviews your health and may include:"],
        list: [
          "A review of your health history and lifestyle",
          "Blood pressure and basic vitals",
          "Age-appropriate screening",
          "Discussion of any symptoms or risk factors",
        ],
      },
      {
        heading: "Who benefits most",
        body: [
          "Checkups are especially useful as you age or if you have risk factors such as a family history, a chronic condition, or lifestyle habits that affect health.",
        ],
      },
      {
        heading: "A proactive choice",
        body: [
          "A checkup is a positive, proactive step — not a sign of worry. It gives you space to understand your health and plan for the future.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should I have a health checkup?",
        a: "This depends on your age, health and risk factors. A consultation can help you decide an appropriate schedule.",
      },
      {
        q: "Are health checkups only for older people?",
        a: "No. Checkups are valuable at any age, especially with risk factors. The frequency varies by age and health.",
      },
    ],
    seo: {
      title: "Importance of Regular Health Checkups | AV Clinic",
      description:
        "Learn why regular health checkups in Krishnagiri matter and what a good preventive checkup covers. Health guidance at AV Clinic.",
    },
  },
  {
    slug: "healthy-lifestyle-adults",
    title: "Healthy Lifestyle Habits for Adults",
    category: "General Health",
    excerpt:
      "Simple daily habits have a big impact on long-term health. Learn the essentials of a healthy adult lifestyle.",
    image: img.svcMensHealth,
    imageAlt: "Healthy lifestyle habits for adults",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-27",
    modified: "2026-08-27",
    readTime: "4 min read",
    tags: ["healthy lifestyle", "wellness", "preventive health", "daily habits"],
    primaryKeyword: "healthy lifestyle habits",
    secondaryKeywords: ["healthy habits adults", "wellness tips", "preventive health"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["regular-health-checkups", "sleep-health", "stress-management-health"],
    sections: [
      {
        heading: "Small habits, big impact",
        body: [
          "Long-term health is built on consistent daily habits rather than occasional effort. Even simple changes can make a meaningful difference over time.",
        ],
      },
      {
        heading: "Core habits to build",
        body: ["These habits support overall health:"],
        list: [
          "A balanced diet with plenty of vegetables and fruit",
          "Regular physical activity",
          "Adequate sleep",
          "Managing stress",
          "Limiting alcohol and avoiding smoking",
        ],
      },
      {
        heading: "Consistency over perfection",
        body: [
          "You do not need a perfect routine. Building healthy habits gradually and keeping them consistent is far more effective than striving for perfection.",
        ],
      },
      {
        heading: "Listen to your body",
        body: [
          "Alongside good habits, pay attention to how you feel and seek care for persistent or concerning symptoms. Prevention and early action go hand in hand.",
        ],
      },
    ],
    faqs: [
      {
        q: "Where do I start with a healthy lifestyle?",
        a: "Start small — add more vegetables, take regular walks and improve sleep. Build from there gradually.",
      },
      {
        q: "How much exercise do adults need?",
        a: "Aim for regular activity most days of the week, combining moderate exercise with some strength work. Even brisk walking counts.",
      },
    ],
    seo: {
      title: "Healthy Lifestyle Habits for Adults | AV Clinic",
      description:
        "Learn the core healthy lifestyle habits for adults — diet, exercise, sleep and stress. Preventive health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "drinking-water",
    title: "Importance of Drinking Enough Water",
    category: "General Health",
    excerpt:
      "Staying hydrated supports every part of your body, including your kidneys. Learn how much water you need and why it matters.",
    image: img.svcKidneyStones,
    imageAlt: "Importance of drinking enough water",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-23",
    modified: "2026-08-23",
    readTime: "4 min read",
    tags: ["hydration", "water", "kidney health", "general health"],
    primaryKeyword: "importance of drinking water",
    secondaryKeywords: ["how much water to drink", "hydration", "water and kidney health"],
    relatedService: "kidney-stones",
    relatedServiceLabel: "Kidney Health",
    relatedArticles: ["kidney-stone-prevention", "kidney-health", "bladder-health-tips"],
    sections: [
      {
        heading: "Water supports your whole body",
        body: [
          "Water is essential for nearly every body function — from flushing waste through the kidneys to regulating temperature and supporting digestion.",
        ],
      },
      {
        heading: "Why hydration matters for the kidneys",
        body: [
          "Drinking enough water keeps urine dilute, which helps the kidneys flush out waste and reduces the concentration of stone-forming minerals. This is a key step in preventing kidney stones.",
        ],
      },
      {
        heading: "How much do you need?",
        body: [
          "Needs vary with body size, activity and climate. A general guide is around 1.5 to 2 litres a day, more if you are active or in a hot climate. Aim for pale, dilute urine as a simple check.",
        ],
      },
      {
        heading: "Simple ways to hydrate",
        body: [
          "Keep a water bottle nearby, drink at meals and increase water in warm weather or with exercise. Limit sugary drinks, which add extra sugar.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much water should I drink daily?",
        a: "A general guide is around 1.5 to 2 litres, adjusted for activity, climate and your needs. Your doctor can advise for your situation.",
      },
      {
        q: "Does drinking water prevent kidney stones?",
        a: "Yes. Adequate hydration is the single most important step to reduce the risk of forming new stones.",
      },
    ],
    seo: {
      title: "Importance of Drinking Enough Water | AV Clinic",
      description:
        "Learn why staying hydrated matters, including for kidney health, and how much water you need. Health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "sleep-health",
    title: "Sleep and Overall Health",
    category: "General Health",
    excerpt:
      "Quality sleep is essential for health and wellbeing. Learn why sleep matters and how to improve it.",
    image: img.svcMensHealth,
    imageAlt: "Sleep and overall health",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-19",
    modified: "2026-08-19",
    readTime: "4 min read",
    tags: ["sleep", "rest", "wellbeing", "general health"],
    primaryKeyword: "sleep and health",
    secondaryKeywords: ["importance of sleep", "sleep hygiene", "better sleep"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["stress-management-health", "healthy-lifestyle-adults", "regular-health-checkups"],
    sections: [
      {
        heading: "Sleep is foundational",
        body: [
          "Sleep allows the body to rest, repair and restore. It supports the immune system, mood, memory, heart health and hormonal balance.",
        ],
      },
      {
        heading: "What poor sleep can do",
        body: ["Chronic poor sleep is linked to:"],
        list: [
          "Reduced energy and focus",
          "Low mood and irritability",
          "Weight gain and appetite changes",
          "Higher risk of some health conditions",
        ],
      },
      {
        heading: "Habits for better sleep",
        body: ["Improve sleep with these habits:"],
        list: [
          "A consistent sleep schedule",
          "A calm, dark, cool bedroom",
          "Limiting screens before bed",
          "Avoiding caffeine and heavy meals late",
          "Regular daytime activity",
        ],
      },
      {
        heading: "When to seek help",
        body: [
          "If sleep problems are persistent or affecting your daily life, seek medical advice. Underlying conditions such as stress or health issues may be worth exploring.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much sleep do adults need?",
        a: "Most adults benefit from around seven to nine hours a night, though individual needs vary.",
      },
      {
        q: "Does screen time affect sleep?",
        a: "Yes. Screens before bed can disrupt sleep. Reducing screen time in the hour before bed can help.",
      },
    ],
    seo: {
      title: "Sleep & Overall Health | AV Clinic",
      description:
        "Learn why quality sleep matters for health and simple habits to sleep better. Health and wellbeing guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "stress-management-health",
    title: "Stress Management and General Health",
    category: "General Health",
    excerpt:
      "Chronic stress affects your body and mind. Learn how to manage stress for better overall health.",
    image: img.svcMensHealth,
    imageAlt: "Stress management and general health",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-15",
    modified: "2026-08-15",
    readTime: "4 min read",
    tags: ["stress", "stress management", "wellbeing", "mental health"],
    primaryKeyword: "stress management",
    secondaryKeywords: ["manage stress", "stress and health", "relaxation techniques"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["sleep-health", "healthy-lifestyle-adults", "stress-fertility"],
    sections: [
      {
        heading: "Stress affects the whole body",
        body: [
          "Stress is a natural response, but when it is prolonged it can affect sleep, digestion, heart health, immunity and mood. Managing it is an important part of overall health.",
        ],
      },
      {
        heading: "Signs of too much stress",
        body: ["Watch for signs such as:"],
        list: [
          "Difficulty sleeping or constant tiredness",
          "Irritability or low mood",
          "Changes in appetite",
          "Tension, headaches or body aches",
          "Difficulty concentrating",
        ],
      },
      {
        heading: "Ways to manage stress",
        body: ["These approaches help:"],
        list: [
          "Regular physical activity",
          "Mindful breathing or relaxation practice",
          "Talking to people you trust",
          "Time outdoors and hobbies",
          "Good sleep and balanced meals",
        ],
      },
      {
        heading: "When to get support",
        body: [
          "If stress is overwhelming or persistent, seeking support is a healthy, positive step. A doctor can help you find the right resources.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can stress make you ill?",
        a: "Prolonged stress can worsen several health conditions and affect wellbeing. Managing it supports your health.",
      },
      {
        q: "What is the quickest way to reduce stress?",
        a: "A short walk, deep breathing or stepping away from the stressor can help in the moment. Consistent habits are best for the long term.",
      },
    ],
    seo: {
      title: "Stress Management & General Health | AV Clinic",
      description:
        "Learn how chronic stress affects your health and practical ways to manage it. Health and wellbeing guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "healthy-diet-skin",
    title: "Healthy Diet and Skin Health",
    category: "Skin Care",
    excerpt:
      "What you eat can support your skin. Learn how a balanced diet contributes to healthy skin from within.",
    image: img.svcSkinRejuvenation,
    imageAlt: "Healthy diet and skin health",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-11",
    modified: "2026-08-11",
    readTime: "4 min read",
    tags: ["diet", "skin health", "nutrition", "skin care"],
    primaryKeyword: "diet and skin health",
    secondaryKeywords: ["foods for healthy skin", "diet for skin", "nutrition skin"],
    relatedService: "skin-rejuvenation",
    relatedServiceLabel: "Skin Rejuvenation",
    relatedArticles: ["dry-skin-causes-care", "sensitive-skin-care", "healthy-lifestyle-adults"],
    sections: [
      {
        heading: "Skin is influenced by nutrition",
        body: [
          "Skin is affected by your overall health, which includes your diet. A balanced intake of vitamins, minerals and healthy fats supports a clear, hydrated complexion.",
        ],
      },
      {
        heading: "Foods that support skin",
        body: ["A skin-friendly diet is rich in:"],
        list: [
          "Vegetables and fruit, which provide antioxidants",
          "Healthy fats, such as nuts, seeds and oily fish",
          "Adequate water",
          "Whole grains and lean protein",
        ],
      },
      {
        heading: "What to limit",
        body: [
          "Diets very high in excess sugar and processed food may not be ideal for skin. Limiting these and staying hydrated supports skin health.",
        ],
      },
      {
        heading: "Diet is one part of the picture",
        body: [
          "Diet supports skin health, but it does not replace a good skin-care routine and sun protection. A balanced approach works best.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can diet improve skin?",
        a: "A balanced diet supports skin health, but it is one part of the picture alongside good skin care and sun protection.",
      },
      {
        q: "Does drinking water improve skin?",
        a: "Hydration supports overall health and skin, though moisturising and sun protection are also important.",
      },
    ],
    seo: {
      title: "Healthy Diet & Skin Health | AV Clinic",
      description:
        "Learn how a balanced diet supports healthy skin and what foods to include. Skin and wellness guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "exercise-mens-health",
    title: "Exercise and Men's Health",
    category: "Men's Health",
    excerpt:
      "Regular exercise supports heart health, weight, energy and men's sexual and reproductive health. Learn why it matters.",
    image: img.svcMensHealth,
    imageAlt: "Exercise and men's health",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-07",
    modified: "2026-08-07",
    readTime: "4 min read",
    tags: ["exercise", "men's health", "fitness", "heart health"],
    primaryKeyword: "exercise and men's health",
    secondaryKeywords: ["men exercise benefits", "fitness men's health", "exercise and testosterone"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["prostate-health-lifestyle", "male-sexual-health-lifestyle", "healthy-lifestyle-adults"],
    sections: [
      {
        heading: "Exercise supports men's health broadly",
        body: [
          "Regular activity benefits the heart, weight, energy, mood and bone health. It also supports men's sexual and reproductive health by improving blood flow and hormonal balance.",
        ],
      },
      {
        heading: "Key benefits",
        body: ["Exercise can help with:"],
        list: [
          "Heart and circulatory health",
          "Maintaining a healthy weight",
          "Energy and sleep",
          "Stress and mood",
          "Erectile function and testosterone support",
        ],
      },
      {
        heading: "What kind of exercise",
        body: [
          "A mix of moderate aerobic activity — such as brisk walking or cycling — and strength training is ideal. Even regular walking makes a meaningful difference.",
        ],
      },
      {
        heading: "Start where you are",
        body: [
          "You do not need an intense routine. Start at a level you can sustain and build gradually. Consistency beats intensity.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much exercise do men need?",
        a: "Aim for regular activity most days, combining moderate aerobic exercise with strength work. Start at a comfortable level.",
      },
      {
        q: "Does exercise help erectile function?",
        a: "Yes. Regular activity improves blood flow and cardiovascular health, which can support erectile function.",
      },
    ],
    seo: {
      title: "Exercise & Men's Health | AV Clinic",
      description:
        "Learn how regular exercise supports men's health — heart, weight, mood and reproductive health. Health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "exercise-womens-health",
    title: "Exercise and Women's Health",
    category: "Women's Health",
    excerpt:
      "Regular exercise supports women's heart, bone and hormonal health. Learn why it matters and how to get started.",
    image: img.svcMensHealth,
    imageAlt: "Exercise and women's health",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-03",
    modified: "2026-08-03",
    readTime: "4 min read",
    tags: ["exercise", "women's health", "fitness", "bone health"],
    primaryKeyword: "exercise and women's health",
    secondaryKeywords: ["women exercise benefits", "fitness women's health", "exercise and hormones"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["healthy-lifestyle-adults", "stress-management-health", "irregular-periods-and-womens-health"],
    sections: [
      {
        heading: "Exercise supports women's health at every age",
        body: [
          "Regular physical activity benefits the heart, bones, weight, mood and hormonal balance. It is a cornerstone of long-term health for women.",
        ],
      },
      {
        heading: "Key benefits",
        body: ["Exercise can help with:"],
        list: [
          "Heart and bone health",
          "Maintaining a healthy weight",
          "Energy, sleep and mood",
          "Reducing stress",
          "Managing hormonal changes over time",
        ],
      },
      {
        heading: "Strength matters",
        body: [
          "Alongside aerobic activity, strength training supports bone health, which is especially important as women age. Simple resistance work is very valuable.",
        ],
      },
      {
        heading: "Make it sustainable",
        body: [
          "Choose activities you enjoy and can maintain. Regular, moderate activity is more beneficial than occasional intense sessions.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much exercise do women need?",
        a: "Aim for regular activity most days, combining aerobic exercise with strength work a couple of times a week.",
      },
      {
        q: "Does exercise help with hormonal health?",
        a: "Yes. Regular activity supports hormonal balance and helps manage stress and weight, which are important for health.",
      },
    ],
    seo: {
      title: "Exercise & Women's Health | AV Clinic",
      description:
        "Learn how regular exercise supports women's health — heart, bones, mood and hormonal balance. Health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "symptoms-not-to-ignore",
    title: "Common Health Symptoms You Should Not Ignore",
    category: "General Health",
    excerpt:
      "Some symptoms deserve attention rather than being dismissed. Learn the signs worth getting checked.",
    image: img.svcUroConsult,
    imageAlt: "Common health symptoms you should not ignore",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-30",
    modified: "2026-07-30",
    readTime: "5 min read",
    tags: ["symptoms", "warning signs", "early detection", "general health"],
    primaryKeyword: "health symptoms not to ignore",
    secondaryKeywords: ["warning signs", "symptoms to check", "when to see a doctor"],
    relatedService: "urological-consultation",
    relatedServiceLabel: "Health Consultation",
    relatedArticles: ["blood-in-urine", "when-to-see-urologist", "skin-infection-warning-signs"],
    sections: [
      {
        heading: "Some symptoms deserve a check-up",
        body: [
          "Your body often gives early signals. Some symptoms can be dismissed, but others are worth getting evaluated rather than waiting.",
        ],
      },
      {
        heading: "Signs worth getting checked",
        body: ["Consider medical advice for:"],
        list: [
          "Unexplained weight loss",
          "Blood in the urine or stool",
          "Persistent pain that does not settle",
          "A lump or swelling that does not go away",
          "Fever that keeps returning",
          "Persistent changes in urination or bowel habits",
        ],
      },
      {
        heading: "Why early matters",
        body: [
          "Many conditions are easier to manage when found early. Seeking advice for a concerning symptom is a sensible, proactive step.",
        ],
      },
      {
        heading: "Trust your instincts",
        body: [
          "If something does not feel right and it persists, it is reasonable to have it checked. You do not need to wait for it to become serious.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should I not ignore a symptom?",
        a: "If a symptom is persistent, worsening, unusual or accompanied by other changes, get it checked.",
      },
      {
        q: "Should I see a doctor for blood in urine?",
        a: "Yes. Blood in the urine should always be evaluated by a doctor.",
      },
    ],
    seo: {
      title: "Health Symptoms You Should Not Ignore | AV Clinic",
      description:
        "Learn the common health symptoms worth getting checked and why early action matters. Health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "choose-right-specialist",
    title: "How to Choose the Right Specialist for Your Health Concern",
    category: "General Health",
    excerpt:
      "Choosing the right specialist makes a difference. Learn how to match your concern to the right type of doctor.",
    image: img.svcUroConsult,
    imageAlt: "How to choose the right specialist",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-26",
    modified: "2026-07-26",
    readTime: "5 min read",
    tags: ["choose specialist", "doctor", "healthcare", "general health"],
    primaryKeyword: "choose the right specialist",
    secondaryKeywords: ["how to choose a doctor", "medical specialist", "healthcare Krishnagiri"],
    relatedService: "urological-consultation",
    relatedServiceLabel: "Specialist Consultation",
    relatedArticles: ["when-to-see-urologist", "when-to-see-dermatologist", "when-to-see-andrologist"],
    sections: [
      {
        heading: "Matching the specialist to the concern",
        body: [
          "Different health concerns are best handled by the right type of specialist. Knowing who to see can save time and ensure you get the most appropriate care.",
        ],
      },
      {
        heading: "Common examples",
        body: ["Here is a simple guide:"],
        list: [
          "Skin, hair and nail concerns — a dermatologist",
          "Kidney, bladder and prostate concerns — a urologist",
          "Male fertility and sexual health — an andrologist",
          "Women's reproductive concerns — a gynaecologist",
          "General checkups and prevention — your primary doctor",
        ],
      },
      {
        heading: "What to look for",
        body: [
          "Look for a qualified specialist with experience in the concern, and a clinic where you feel comfortable discussing things openly. Clear communication and patient education matter.",
        ],
      },
      {
        heading: "Start with a consultation",
        body: [
          "If you are unsure who to see, a consultation can guide you. A doctor can recommend the appropriate specialist for your concern.",
        ],
      },
    ],
    faqs: [
      {
        q: "Who should I see for skin problems?",
        a: "A dermatologist specialises in skin, hair and nail conditions.",
      },
      {
        q: "Who should I see for urinary or kidney problems?",
        a: "A urologist specialises in kidney, bladder and prostate concerns.",
      },
    ],
    seo: {
      title: "How to Choose the Right Specialist | AV Clinic",
      description:
        "Learn how to choose the right specialist in Krishnagiri for your health concern — skin, urology, fertility and more. Guidance from AV Clinic.",
    },
  },
];